import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Copy, ExternalLink, Star, TrendingUp, Users } from 'lucide-react';

const ReferralModal = ({ isOpen, onClose, friend, onGenerateReferral, onGenerateProfileShare }) => {
  const [referralData, setReferralData] = useState({
    service: '',
    message: ''
  });
  const [referralResult, setReferralResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isProfileShareLoading, setIsProfileShareLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!referralData.service) return;

    setIsLoading(true);
    try {
      const requestData = {
        friend_id: String(friend.id), // Ensure it's a string
        service: referralData.service || '',
        message: referralData.message || ''
      };
      
      console.log('Sending referral request:', requestData); // Debug request data
      console.log('Friend object:', friend); // Debug friend object
      
      const result = await onGenerateReferral(requestData);
      
      console.log('Referral API result:', result); // Debug log
      console.log('Social proof content:', result.social_proof); // Debug social proof specifically
      
      // Just use the result directly - backend testing confirmed it works
      setReferralResult(result);
    } catch (error) {
      console.error('Error generating referral:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      const errorMessage = error.response?.data?.detail || error.message || error;
      alert(`Error generating referral: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setReferralData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    });
  };

  const handleProfileShare = async () => {
    if (!onGenerateProfileShare) return;
    
    setIsProfileShareLoading(true);
    try {
      const result = await onGenerateProfileShare(friend.id);
      console.log('Profile share result:', result);
      
      // Set the result to display
      setReferralResult({
        status: result.status,
        referral_link: result.share_link,
        referral_code: result.share_code,
        friend_name: result.friend_name,
        service: 'Profile Sharing',
        message: `Shared ${friend.name}'s professional profile`,
        social_proof: result.social_proof
      });
    } catch (error) {
      console.error('Error generating profile share:', error);
      const errorMessage = error.response?.data?.detail || error.message || error;
      alert(`Error generating profile share: ${errorMessage}`);
    } finally {
      setIsProfileShareLoading(false);
    }
  };

  const handleClose = () => {
    setReferralResult(null);
    setReferralData({ service: '', message: '' });
    onClose();
  };

  if (!friend) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" style={{color: 'var(--brand-tan)'}} />
            Share Profile - {friend.name}
          </DialogTitle>
        </DialogHeader>

        {!referralResult ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Friend Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                  style={{
                    background: 'linear-gradient(135deg, var(--brand-navy) 0%, var(--brand-navy-light) 100%)'
                  }}
                >
                  {friend.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="font-medium" style={{color: 'var(--brand-navy)'}}>
                    {friend.name}
                  </div>
                  {friend.company && (
                    <div className="text-sm opacity-70" style={{color: 'var(--brand-navy)'}}>
                      {friend.company}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-4 text-sm">
                <span>📞 {friend.phone || 'N/A'}</span>
                <span>📧 {friend.email || 'N/A'}</span>
              </div>
            </div>

            {/* Profile Share Option */}
            {onGenerateProfileShare && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-green-900 mb-1">Share {friend.name}'s Profile</h4>
                    <p className="text-sm text-green-700">Generate a direct link to their professional profile</p>
                  </div>
                  <Button
                    type="button"
                    onClick={handleProfileShare}
                    disabled={isProfileShareLoading}
                    className="bg-green-600 hover:bg-green-700 text-white px-6"
                  >
                    {isProfileShareLoading ? 'Creating Link...' : 'Create Share Link'}
                  </Button>
                </div>
              </div>
            )}

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-muted-foreground">Or create custom referral</span>
              </div>
            </div>

            {/* Service Selection */}
            <div>
              <Label htmlFor="service">Service to Recommend *</Label>
              <Select onValueChange={(value) => handleInputChange('service', value)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select service..." />
                </SelectTrigger>
                <SelectContent>
                  {friend.services?.map(service => (
                    <SelectItem key={service} value={service}>
                      {service}
                    </SelectItem>
                  ))}
                  <SelectItem value="General Services">General Services</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Custom Message */}
            <div>
              <Label htmlFor="message">Personal Recommendation (Optional)</Label>
              <Textarea
                id="message"
                value={referralData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                placeholder={`Why do you recommend ${friend.name} for ${referralData.service || 'this service'}?`}
                className="mt-1"
                rows={3}
              />
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-blue-800">
                  What's a Referral Link?
                </span>
              </div>
              <p className="text-sm text-blue-600">
                Create a shareable link with social proof that showcases {friend.name}'s 
                credibility, ratings, and your personal recommendation. Perfect for helping 
                others discover trusted professionals.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 pt-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={!referralData.service || isLoading}
                className="flex-1 text-white"
                style={{
                  background: 'linear-gradient(135deg, var(--brand-tan) 0%, var(--brand-tan-dark) 100%)'
                }}
              >
                {isLoading ? 'Generating...' : 'Generate Referral Link'}
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            {/* Success Message */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-800">
                  Referral link generated successfully!
                </span>
              </div>
              <p className="text-sm text-green-600">
                Share this link to refer {referralResult.friend_name} for {referralResult.service}
              </p>
            </div>

            {/* Referral Link */}
            <div>
              <Label>Referral Link</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={referralResult.referral_link}
                  readOnly
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => copyToClipboard(referralResult.referral_link)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Social Proof Preview */}
            <div>
              <Label>Social Proof Preview</Label>
              <div className="mt-1 p-4 bg-gray-50 rounded-lg border">
                <pre className="text-sm whitespace-pre-wrap text-gray-700">
                  {referralResult.social_proof || 'Social proof content will appear here...'}
                </pre>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => copyToClipboard(referralResult.social_proof)}
                className="w-full"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Content
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => window.open(referralResult.referral_link, '_blank')}
                className="w-full"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Preview Link
              </Button>
            </div>

            <div className="flex gap-2">
              <Button 
                type="button" 
                onClick={handleClose}
                className="w-full text-white"
                style={{
                  background: 'linear-gradient(135deg, var(--brand-tan) 0%, var(--brand-tan-dark) 100%)'
                }}
              >
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReferralModal;